import { useEffect, useState, type Dispatch } from 'react';

import type { LoggerMode } from './loggerMode';
import type { DraftSet, LoggerAction } from './state/loggerDraftTypes';
import { failToggleFields } from './state/loggerDraftHelpers';
import {
  getEffortIntroSeen,
  loadEffortIntroSeen,
  markEffortIntroSeen,
} from './state/effortMeterIntro';
import { markBandStopSeen } from './state/holdBandStopHint';
import { canonicalRirOf } from './state/canonicalRir';
import { dismissSoftKeyboard } from '../primitives/dismissSoftKeyboard';

// Effort-meter commit composition (AP-0b). The meter's tap IS the completion
// gesture, so this hook routes each tap onto the EXISTING legs — it never
// duplicates the completion chain (useSetCyclePill stays the single owner of
// PR check + MAX regression + rest timer + edit-mode placement):
//   pending set  + segment → onLogSet(rir): effort write + full commit leg.
//     …and if a hold/cardio timer is counting on that set, the SAME tap stops
//     it first (captureTimer) and the seconds ride into the same write. That is
//     the whole of MX-5 slice 3: a timed set is completed by the effort meter
//     like every other kind, instead of by a ✓ on a banner that never asked
//     how it felt (AP-0e).
//   pending set  + Fail    → onLogFail(): unchanged fail-commit leg.
//   logged set   + segment → update_set effort EDIT — updates rir (and clears
//     a fail: the band is a radio group, picking an effort means the set WAS
//     completed) but NEVER re-stamps completedAt (timeline integrity,
//     next/timeline.md — same invariant as onToggleFail).
//   logged set   + Fail    → onToggleFail(): flip done ↔ failed in place.
// The first REAL tap ever also opens the effort-scale sheet once (persisted
// flag — see effortMeterIntro) so the band explains itself at the exact
// moment it's first used.
//
// ⭐ LG-69 — a tap that COMPLETES a set dismisses the keyboard. Antenore,
// 2026-09-04: «la maggioranza degli utenti vorranno che la tastiera si chiuda a
// meno che non sto digitando qualcosa, ovvero non nel caso in cui committo
// l'effort». The reason it matters is what the keyboard sits on top of: the
// rest bar is docked to the BOTTOM edge of the screen (LoggerRestBar), which is
// exactly where the keyboard is, and the focus advance is deferred to the end
// of the rest (useRestAdvance) — so without this the keyboard would cover the
// countdown, and the row that just landed in the timeline, for the whole rest,
// with nothing left to type into. Nothing is lost by blurring: FocusPanel
// writes every keystroke into the draft, not on blur.
//
// ⛔ Only on a tap that really COMMITTED, which is why both legs below check.
// `commitSet` refuses an empty-reps AMRAP and answers with a prompt that asks
// the athlete to TYPE the reps: dismissing there would close the keyboard at
// the exact moment the app is asking for it. The effort EDIT of an
// already-logged set does not dismiss either — no rest arms, nothing new
// appears, and the athlete who went back to a logged set is usually mid-fix.
//
// ⛔ And it lives HERE, on the gesture, not down in `commitSet`: a set can also
// complete on its own when a prescribed bout hits zero (HI-1, through the very
// same onLogSet), and an automatic event must not pull the keyboard out from
// under someone who is typing. The dismissal belongs to the tap that caused it
// ([[feedback_extracting_shared_state_narrows_a_guarantee]]).
//
// PRESCRIBE is the fourth case, and it is none of the above (PE-22 fetta C).
// Authoring a program asks the band a different question — "how hard should
// this set be" — and every leg above answers "how hard WAS it". So the tap
// raises `onPrescribeEffort(rir)` and stops: no completion, no clock, no PR
// check, no rest, and no Fail, because a set that has not been performed
// cannot have been failed.
//
// ⛔ It deliberately does NOT write `DraftSet.rirPrescribed`, tempting as that
// looks. That field is the display-only MIRROR the card reads to draw a target
// (loggerDraftTypes.ts: "NEVER persisted — buildPayload does not carry it"),
// and `grep -c rirPrescribed packages/shared/src/programEditBuild.ts` is 0. A
// program says effort as `SetIntensity {kind:'rir', target}`, a different field
// on a different model. Writing the mirror would look right in every pixel of
// the UI and lose the value at save time, silently. So the mount that knows
// which model it is authoring is the one that catches the value.

/** Canonical rir of a draft set for the meter's `value`: the rir string when
 *  present, else derived from a legacy rpe (edit/repeat hydration may carry
 *  either — same mirror rule the old effort TextInput used). */
export function draftSetRirValue(set: DraftSet | null): number | null {
  if (!set) return null;
  return canonicalRirOf(set.rir, set.rpe);
}

export function useEffortCommit(args: {
  boundSet: DraftSet | null;
  /** Which question the band is answering. `prescribe` takes the branch
   *  described above; `log`/`log-past` are the measurement legs, unchanged. */
  mode: LoggerMode;
  /** Where a prescribed effort goes. Supplied by the mount that knows which
   *  model it writes — the program composer routes it to the slot's
   *  `SetIntensity`. Absent in every logger mount, and absent is correct
   *  there: nothing in a live session prescribes. */
  onPrescribeEffort?: (rir: number) => void;
  activeEntryIdx: number;
  boundSetIdx: number;
  dispatch: Dispatch<LoggerAction>;
  /** useTimelineLogger.onLogSet — with the tapped rir it writes the effort
   *  and threads it into cycleSetPill (fresh-rir MAX-regression check). The
   *  second argument carries the held seconds when the tap also stopped a
   *  timer, so both land in ONE update_set. */
  onLogSet: (rir?: number, durationSec?: number) => { prFlash: boolean } | undefined;
  /** Returns whether the set was really completed — false when the empty-reps
   *  AMRAP gate refused the tap (LG-69). */
  onLogFail: () => boolean;
  onToggleFail: () => void;
  /** Stops a hold/cardio timer running on the bound set and returns the seconds
   *  held; null when nothing was running (useHoldTimer.capture). Optional — the
   *  card logger and the tests have no timer to stop. */
  captureTimer?: () => number | null;
  /** LG-78 — run beside `dismissSoftKeyboard()`, to lower the field-focus
   *  signal by declaration instead of waiting for a blur that RN does not
   *  always deliver (see useFieldFocus §releaseAll). Absent in the prescribe
   *  mount, which has no rest advance waiting on it. Also names the row that
   *  landed (#225). */
  onKeyboardDismissed?: (entryIdx: number, setIdx: number) => void;
  /** Opens the EffortScaleModal — reused for the once-ever intro. */
  onOpenEffortScale: () => void;
  /** True when this tap will raise the AMRAP-confirm prompt (LG-26). Used to
   *  HOLD BACK the once-ever intro, never to change what gets written. */
  willPromptAmrap?: (entryIdx: number, setIdx: number) => boolean;
}) {
  const {
    boundSet,
    mode,
    onPrescribeEffort,
    activeEntryIdx,
    boundSetIdx,
    dispatch,
    onLogSet,
    onLogFail,
    onToggleFail,
    onOpenEffortScale,
    captureTimer,
    willPromptAmrap,
  } = args;

  // PR gold pulse on the tapped segment. Keyed so a repeat PR re-fires; kept
  // here (not derived from set.prFlash) because the commit ADVANCES the
  // binding immediately — by the next render the meter binds the next pending
  // set, so the flash must outlive the tapped set's binding.
  const [flash, setFlash] = useState<{ rir: number; key: number }>({
    rir: -1,
    key: 0,
  });

  useEffect(() => {
    void loadEffortIntroSeen();
  }, []);

  const introOnFirstTap = () => {
    if (getEffortIntroSeen()) return;
    void markEffortIntroSeen();
    onOpenEffortScale();
  };

  const onSelect = (rir: number) => {
    if (activeEntryIdx < 0 || boundSetIdx < 0 || !boundSet) return;
    if (mode === 'prescribe') {
      // The intro still fires: the scale needs explaining the first time it is
      // used, and prescribing is using it. Everything below this line is about
      // a set that happened, so none of it runs.
      introOnFirstTap();
      onPrescribeEffort?.(rir);
      return;
    }
    // Hold the intro back when this same tap is about to raise the AMRAP
    // question (LG-26). Same rule onFail already applies below, and for the
    // same reason: two modals on one gesture is a bug this file has already
    // been fixed for once (Antenore 2026-07-06). The intro is not lost — the
    // flag is only marked when it actually opens, so it fires on the next tap.
    if (!willPromptAmrap?.(activeEntryIdx, boundSetIdx)) introOnFirstTap();
    if (boundSet.completedAt === null) {
      // Stop the count BEFORE committing: the seconds have to be in hand when
      // the write is queued, or they'd need a second update_set behind it (two
      // dispatches for one gesture, and the completion would land on a set
      // whose duration wasn't written yet).
      const heldSec = captureTimer?.() ?? null;
      // They have now stopped a timer with the band once, so the caption that
      // teaches the gesture retires (TM-10). Marked on the DOING, not on the
      // showing.
      if (heldSec !== null) void markBandStopSeen();
      const committed = onLogSet(rir, heldSec ?? undefined);
      // `undefined` is the empty-reps AMRAP refusal, not a commit — see the
      // header. Only a real completion closes the keyboard.
      if (committed) {
        dismissSoftKeyboard();
        args.onKeyboardDismissed?.(activeEntryIdx, boundSetIdx);
      }
      if (committed?.prFlash) setFlash((f) => ({ rir, key: f.key + 1 }));
      return;
    }
    // A logged set has no timer running on it (capture cleared it when the set
    // was closed), so the edit leg below never has seconds to fold in.
    // Effort EDIT on a logged set — value change only, completedAt untouched.
    dispatch({
      type: 'update_set',
      entryIdx: activeEntryIdx,
      setIdx: boundSetIdx,
      patch: {
        rir: String(rir),
        rpe: '',
        // Picking an effort segment means the set WAS completed → clear the
        // fail label (AP-0p model). reps are unchanged.
        ...(boundSet.fail ? failToggleFields(false) : {}),
      },
    });
  };

  const onFail = () => {
    if (activeEntryIdx < 0 || boundSetIdx < 0 || !boundSet) return;
    // A prescription cannot fail. "To failure" IS prescribable, but it is the
    // other axis — what ENDS the set, not how hard it was (mockup §01, «bound
    // by»), and it is AMRAP's job. The mount hides the segment; this is the
    // guard for anyone who reaches the callback anyway.
    if (mode === 'prescribe') return;
    // NO effort-scale intro here (AP-0p): the intro explains the easy→hard
    // BAND, but Fail sits outside it, and a fail on a rep-based set opens the
    // clean-reps prompt — firing both at once stacked two modals (Antenore
    // 2026-07-06). The intro still fires on the first real segment pick.
    if (boundSet.completedAt === null) {
      // A fail is a completion like any other (Antenore 2026-07-20), so it
      // closes the keyboard the same way — but only when it landed.
      if (onLogFail()) {
        dismissSoftKeyboard();
        args.onKeyboardDismissed?.(activeEntryIdx, boundSetIdx);
      }
      return;
    }
    onToggleFail();
  };

  return {
    // The meter shows RECORDED effort, and now that is all it CAN show.
    //
    // ⛔ The gate that used to stand here (`completedAt == null ? null : …`,
    // 2026-07-06) was a cure for a wound that no longer exists: the plan and
    // the repeat both wrote their prescription into `rpe`/`rir`, so a pending
    // set arrived carrying a value it had not earned, and a filled segment
    // read as "already judged". PE-22 fetta D moved the prescription to its
    // own half (`rirPrescribed`), so a pending set's `rpe`/`rir` are empty by
    // construction and draftSetRirValue returns null on its own. Keeping the
    // gate would hide whether the divorce actually holds — a provisional cure
    // that outlives its replacement stops anyone noticing the replacement
    // broke. The target's own rendering is AP-1 (ghost, never selected).
    // In prescription the band shows the TARGET as its own value, selected
    // like any other pick — it is what is being authored. The dashed ghost is
    // the LOGGER's way of drawing someone else's target (D2), and the two must
    // not be confused: here there is no measurement to sit beside it.
    value:
      mode === 'prescribe'
        ? (boundSet?.rirPrescribed ?? null)
        : draftSetRirValue(boundSet),
    failed: mode !== 'prescribe' && boundSet?.fail === true,
    onSelect,
    onFail,
    flashRir: flash.rir,
    flashKey: flash.key,
  };
}
